/**
 * useExportManager.ts
 * 
 * Manages video exports (client-side and server-side).
 * Extracted from App.tsx monolith to reduce code complexity.
 */

import React, { useState, useRef, useCallback, useMemo, useEffect, MutableRefObject, SetStateAction, Dispatch } from 'react';
import { ChunkStatus, createInitialChunks, downloadChunkToFFmpegFS, isChunkDownloading, mergeChunksWithFFmpeg, addOverlaysToMergedVideo, detectOverlayClips } from '../client-merge';
import { Clip, Track, SubtitleStyle } from '../types';
import { AspectRatio, ASPECT_RATIOS } from '../media-recorder-exporter';
import { ExportJobStatus } from '../server-export-client';
import { ExportManager, ExportError } from '../export-manager';
import { getServerExportClient } from '../server-export-client';
import { generateSRT, generateSRTFromClips } from '../utils/srt';
import { generateThumbnailFromUrl } from '../utils';
import { getClipLocalSourceUrl } from '../utils/clip-helpers';
import { buildTimelineCutPoints, getClipsForChunk } from '../utils/timeline-utils';
import { functions } from '../services/firebase-api';
import { saveProjectToFirestore } from '../firestore';
import firebase from 'firebase/compat/app';
import { createRunPodClient, RunPodExportClient } from '../runpod-export-client';

// RunPod GPU client (singleton)
let runpodClient: RunPodExportClient | null = null;
function getRunPodClient(): RunPodExportClient | null {
    if (runpodClient === null) {
        runpodClient = createRunPodClient(); // Always try to create - fallbacks are in createRunPodClient
    }
    return runpodClient;
}

const isGPUExportEnabled = () => false; // Disabled — using Constance-style burn-subtitles flow

export interface ServerExportState {
    isActive: boolean;
    jobId?: string;
    projectId?: string;
    status?: ExportJobStatus['status'];
    progress?: number;
    currentStep?: string;
    downloadUrl?: string;
    error?: string;
    chunks?: ChunkStatus[];
}

// Extended type for client-side use (includes optional fields)
interface ExportedVideoLocal {
    id: string;
    name: string;
    urlWebM?: string;
    urlMP4?: string;
    thumbnailUrl?: string;
    mp4ConversionProgress?: number;
    srtContent?: string;
}

const createChunkState = (index: number, mode?: ChunkStatus['mode']): ChunkStatus => ({
    index,
    mode,
    serverStatus: 'pending',
    serverProgress: 0,
    url: null,
    localStatus: 'waiting',
    localProgress: 0,
    size: 0,
});

const chunkMatchesUpdates = (chunk: ChunkStatus, updates: Partial<ChunkStatus>): boolean => {
    for (const [key, value] of Object.entries(updates) as [keyof ChunkStatus, ChunkStatus[keyof ChunkStatus]][]) {
        if (chunk[key] !== value) return false;
    }
    return true;
};

const areChunkListsEqual = (left: ChunkStatus[], right: ChunkStatus[]): boolean => {
    if (left === right) return true;
    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i++) {
        const a = left[i];
        const b = right[i];
        if (
            a.index !== b.index ||
            a.mode !== b.mode ||
            a.serverStatus !== b.serverStatus ||
            a.serverProgress !== b.serverProgress ||
            a.url !== b.url ||
            a.localStatus !== b.localStatus ||
            a.localProgress !== b.localProgress ||
            a.size !== b.size
        ) {
            return false;
        }
    }

    return true;
};

export interface UseExportManagerDeps {
    // Timeline data
    allClips: Clip[];
    tracks: Track[];

    // Subtitle config
    activeSubtitleLanguage: string;
    subtitleStyle: SubtitleStyle;

    // Auth/Project context
    user: firebase.User | null;
    currentProjectId: string | null;
    currentProjectIdRef: MutableRefObject<string | null>;

    // For saving/updating projects
    savedProjects: any[];
    setSavedProjects: Dispatch<SetStateAction<any[]>>;

    // For updating clips after auto-upload
    updateClips: (updater: (prev: Clip[]) => Clip[]) => void;
}

export const useExportManager = (deps: UseExportManagerDeps) => {
    const {
        allClips, tracks,
        activeSubtitleLanguage, subtitleStyle,
        user, currentProjectId, currentProjectIdRef,
        savedProjects, setSavedProjects,
        updateClips
    } = deps;

    // ============ STATE ============

    // State
    const [isExporting, setIsExporting] = useState(false);
    const [exportStatus, setExportStatus] = useState('');
    const [exportProgress, setExportProgress] = useState(0);
    const [exportDetails, setExportDetails] = useState<any[]>([]);

    // DEBUG: Verify GPU Config on mount
    React.useEffect(() => {
        const gpuEnabled = import.meta.env.VITE_USE_GPU_EXPORT;
        const gpuUrl = import.meta.env.VITE_RUNPOD_CUSTOM_URL || import.meta.env.VITE_RUNPOD_ENDPOINT_ID;
        console.log('[Config Check] GPU Enabled:', gpuEnabled, 'URL:', gpuUrl);
    }, []);

    // Results
    const [exportedVideos, setExportedVideos] = useState<ExportedVideoLocal[]>([]);

    // Configuration
    const [exportAspectRatio, setExportAspectRatio] = useState<AspectRatio>('16:9');
    const [scaleMode, setScaleMode] = useState<'fit' | 'fill'>('fill');

    // Side-effects / Previews
    const [vfxSegmentPreviews, setVfxSegmentPreviews] = useState<{ id: string; name: string; blobUrl: string; duration: number }[]>([]);

    // Server-side Export State (Array for multiple concurrent jobs)
    const [activeServerExports, setActiveServerExports] = useState<ServerExportState[]>([]);

    // Client-side chunk manager state
    const [chunks, setChunks] = useState<ChunkStatus[]>([]);
    const [isMerging, setIsMerging] = useState(false);
    const chunksRef = useRef<ChunkStatus[]>([]);
    const [hybridMode, setHybridMode] = useState(false);

    const setChunksIfChanged = useCallback((nextChunks: ChunkStatus[]) => {
        setChunks(prev => (areChunkListsEqual(prev, nextChunks) ? prev : nextChunks));
    }, []);

    const updateChunkAtIndex = useCallback((index: number, updates: Partial<ChunkStatus>) => {
        setChunks(prev => {
            const current = prev[index];
            if (!current || chunkMatchesUpdates(current, updates)) return prev;

            const next = [...prev];
            next[index] = { ...current, ...updates };
            return next;
        });
    }, []);

    const upsertChunkAtIndex = useCallback((index: number, updates: Partial<ChunkStatus>, mode?: ChunkStatus['mode']) => {
        setChunks(prev => {
            let next = prev;
            let didGrow = false;

            if (prev.length <= index) {
                next = [...prev];
                while (next.length <= index) {
                    next.push(createChunkState(next.length, mode));
                }
                didGrow = true;
            }

            const current = next[index];
            if (chunkMatchesUpdates(current, updates)) {
                return didGrow ? next : prev;
            }

            if (next === prev) {
                next = [...prev];
            }

            next[index] = { ...current, ...updates };
            return next;
        });
    }, []);

    // Sync ref with state
    useEffect(() => { chunksRef.current = chunks; }, [chunks]);

    // Auto-trigger client-side merge when all chunks are locally downloaded
    useEffect(() => {
        if (chunks.length === 0 || isMerging) return;
        const allDownloaded = chunks.every(c => c.localStatus === 'downloaded');
        if (!allDownloaded) return;

        // Check that at least one active export is waiting for merge
        const pendingMergeJob = activeServerExports.find(j =>
            j.isActive && j.currentStep?.includes('⚙️')
        );
        if (!pendingMergeJob) return;

        const jobId = pendingMergeJob.jobId;
        const totalChunks = chunks.length;

        (async () => {
            setIsMerging(true);
            setActiveServerExports(prev => prev.map(j =>
                j.jobId === jobId ? { ...j, currentStep: hybridMode ? '🔗 Assemblage sur le Cloud Run...' : '🔗 Assemblage local (FFmpeg.wasm)...', progress: 85 } : j
            ));

            try {
                let mergedBlob = await mergeChunksWithFFmpeg(totalChunks);

                // HYBRID MODE: post-process with overlay rendering
                if (hybridMode) {
                    console.log('[HybridExport] Merge done, applying VFX overlays...');
                    setActiveServerExports(prev => prev.map(j =>
                        j.jobId === jobId ? { ...j, currentStep: '🎬 Rendu des effets visuels...', progress: 88 } : j
                    ));
                    mergedBlob = await addOverlaysToMergedVideo(
                        mergedBlob, allClips, tracks, activeSubtitleLanguage, subtitleStyle,
                        (progress, message) => {
                            const scaled = 88 + Math.floor(progress * 0.12);
                            setActiveServerExports(prev => prev.map(j =>
                                j.jobId === jobId ? { ...j, currentStep: `🎬 ${message}`, progress: scaled } : j
                            ));
                        },
                    );
                    setHybridMode(false);
                }

                const blobUrl = URL.createObjectURL(mergedBlob);
                const ext = hybridMode ? '.webm' : '.mp4';
                const exportName = `${hybridMode ? 'Hybrid' : 'Cloud'}_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', 'h')}`;
                const newExport = { id: jobId!, name: exportName, urlMP4: blobUrl };

                setExportedVideos(prev => {
                    if (prev.some(v => v.id === jobId)) return prev;
                    return [...prev, newExport];
                });

                setActiveServerExports(prev => prev.map(j =>
                    j.jobId === jobId ? { ...j, status: 'complete', progress: 100, currentStep: 'Export terminé!', downloadUrl: blobUrl } : j
                ));

                // Auto-download
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `${exportName}${ext}`;
                a.click();

                console.log(`[ClientMerge] ${hybridMode ? 'Hybrid' : 'Cloud'} merge complete: ${(mergedBlob.size / 1024 / 1024).toFixed(1)} MB`);
            } catch (err: any) {
                console.error('[ClientMerge] Merge failed:', err);
                setActiveServerExports(prev => prev.map(j =>
                    j.jobId === jobId ? { ...j, status: 'failed', error: `Merge failed: ${err.message}` } : j
                ));
            } finally {
                setIsMerging(false);
            }
        })();
    }, [chunks, isMerging, activeServerExports]);

    // Refs for cancellation
    const exportManagerRef = useRef<ExportManager | null>(null);
    // Map of jobId -> unsubscribe function
    const serverExportUnsubscribesRef = useRef<Map<string, () => void>>(new Map());

    // ============ COMPUTED ============

    const isTimelineEligibleForFastExport = useMemo(() => {
        const mediaClips = allClips.filter(c => ['video', 'audio'].includes(c.type));
        if (mediaClips.length === 0) return false;

        const hasComplexClips = allClips.some(c => ['text', 'image', 'zoom'].includes(c.type));
        if (hasComplexClips) return false;

        const hasEffects = allClips.some(c =>
            c.fadeInDuration > 0 ||
            c.fadeOutDuration > 0 ||
            (c.transform && (c.transform.scale !== 1 || c.transform.rotation !== 0 || c.transform.x !== 0 || c.transform.y !== 0))
        );
        if (hasEffects) return false;

        const videoUrls = new Set(mediaClips.map(c => c.url));
        if (videoUrls.size > 1) return false;

        if (allClips.some(c => c.trackId === 's1')) return false;

        return true;
    }, [allClips]);

    // ============ HELPERS ============

    const resetExportState = useCallback(() => {
        setIsExporting(false);
        setExportStatus('');
        setExportProgress(0);
        setExportDetails([]);
        setActiveServerExports([]);
        // Clear all subscriptions
        serverExportUnsubscribesRef.current.forEach(unsub => unsub());
        serverExportUnsubscribesRef.current.clear();
    }, []);

    // Upload asset to R2 storage
    const uploadAssetToStorage = async (file: File, clipId?: string, assetId?: string, projectId?: string) => {
        try {
            if (!user) return null;
            console.log(`[Upload] Starting upload for ${file.name}...`);

            const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB chunks
            const rawContentType = file.type || 'application/octet-stream';
            const normalizedContentType = rawContentType.split(';')[0].trim();

            console.log(`[Upload] Mode Multipart (${(file.size / 1024 / 1024).toFixed(1)} MB)`);

            // 1. Init multipart
            const initiateMultipartUpload = functions.httpsCallable('initiateMultipartUpload');
            const initResult = await initiateMultipartUpload({
                fileName: file.name,
                contentType: normalizedContentType
            }) as any;
            const { uploadId, key } = initResult.data;

            // 2. Upload chunks
            const parts: { PartNumber: number; ETag: string }[] = [];
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            const getChunkUploadUrl = functions.httpsCallable('getChunkUploadUrl');

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const urlResult = await getChunkUploadUrl({
                    key,
                    uploadId,
                    partNumber: i + 1,
                    contentType: normalizedContentType
                }) as any;

                const uploadResponse = await fetch(urlResult.data.uploadUrl, {
                    method: 'PUT',
                    body: chunk,
                    headers: { 'Content-Type': normalizedContentType }
                });

                const etag = uploadResponse.headers.get('etag');
                if (etag) {
                    parts.push({ PartNumber: i + 1, ETag: etag.replace(/"/g, '') });
                }
            }

            // 3. Complete multipart
            const completeMultipartUpload = functions.httpsCallable('completeMultipartUpload');
            const completeResult = await completeMultipartUpload({ key, uploadId, parts }) as any;
            const publicUrl = completeResult.data.publicUrl;

            console.log(`[Upload] Complete. Public URL: ${publicUrl}`);

            // Update URL in Clips
            if (clipId) {
                updateClips(prev => prev.map(c => {
                    if (c.id !== clipId) return c;
                    const localUrl = getClipLocalSourceUrl(c);
                    return {
                        ...c,
                        url: publicUrl,
                        ...(localUrl ? { localUrl } : {}),
                    };
                }));
            }

            // Update URL in Project Assets
            if (projectId && assetId) {
                setSavedProjects(prev => prev.map(p => {
                    if (p.id === projectId && p.assets) {
                        const newAssets = p.assets.map((a: any) => a.id === assetId ? { ...a, url: publicUrl } : a);
                        const updated = { ...p, assets: newAssets, updatedAt: Date.now() };
                        saveProjectToFirestore(updated).catch(console.error);
                        return updated;
                    }
                    return p;
                }));
            }

            return publicUrl;
        } catch (err) {
            console.error(`[Upload] Failed to upload ${file.name}`, err);
            return null;
        }
    };

    const prepareTimelineSourceForBurnParallel = useCallback(async (
        clipsToAssemble: Clip[],
        onStatus?: (step: string, progress: number) => void
    ): Promise<string> => {
        const EPSILON = 0.05;
        const visibleClips = clipsToAssemble.filter(clip => {
            const track = tracks.find(t => t.id === clip.trackId);
            return track ? track.isVisible !== false : true;
        });
        const totalVisibleDuration = Math.max(...visibleClips.map(c => c.start + c.duration), 0);
        const mainVideoClips = visibleClips.filter(c => c.type === 'video' && !c.clipRole);
        const overlayClips = visibleClips.filter(c =>
            c.clipRole === 'intro' ||
            c.clipRole === 'outro' ||
            c.type === 'text' ||
            c.type === 'image' ||
            c.type === 'zoom' ||
            c.type === 'synthe' ||
            c.type === 'panel'
        );

        const canReuseRemoteSource = (clip: Clip): boolean => {
            const transform = clip.transform;
            const hasNeutralTransform =
                !transform ||
                (
                    Math.abs(transform.x) < EPSILON &&
                    Math.abs(transform.y) < EPSILON &&
                    Math.abs(transform.scale - 1) < EPSILON &&
                    Math.abs(transform.rotation) < EPSILON
                );
            const hasNeutralEndTransform =
                !clip.endTransform ||
                (
                    Math.abs(clip.endTransform.x) < EPSILON &&
                    Math.abs(clip.endTransform.y) < EPSILON &&
                    Math.abs(clip.endTransform.scale - 1) < EPSILON &&
                    Math.abs(clip.endTransform.rotation) < EPSILON
                );
            const remoteSource = [clip.url, clip.streamUrl].find((url): url is string => !!url && !url.startsWith('blob:'));

            return !!remoteSource &&
                Math.abs(clip.start) < EPSILON &&
                Math.abs((clip.start + clip.duration) - totalVisibleDuration) < EPSILON &&
                Math.abs(clip.trimStart || 0) < EPSILON &&
                Math.abs(clip.duration - (clip.originalDuration || clip.duration)) < EPSILON &&
                Math.abs((clip.playbackRate ?? 1) - 1) < EPSILON &&
                Math.abs((clip.opacity ?? 1) - 1) < EPSILON &&
                Math.abs(clip.fadeInDuration || 0) < EPSILON &&
                Math.abs(clip.fadeOutDuration || 0) < EPSILON &&
                (!clip.activeAudioSource || clip.activeAudioSource === 'original') &&
                hasNeutralTransform &&
                hasNeutralEndTransform;
        };

        if (mainVideoClips.length === 1 && overlayClips.length === 0 && canReuseRemoteSource(mainVideoClips[0])) {
            const reusableSource = [mainVideoClips[0].url, mainVideoClips[0].streamUrl]
                .find((url): url is string => !!url && !url.startsWith('blob:'));

            if (reusableSource) {
                console.log('[BurnParallel] Reusing remote source URL, skipping local timeline preassembly');
                onStatus?.('Source distante réutilisée pour le burn parallèle...', 20);
                return reusableSource;
            }
        }

        // Validate local blob media upfront to avoid silent failures during concat prep.
        const localBlobClips = clipsToAssemble.filter(c => {
            const localSource = getClipLocalSourceUrl(c);
            return !!localSource &&
                localSource.startsWith('blob:') &&
                c.type !== 'text' &&
                c.type !== 'synthe' &&
                c.type !== 'panel';
        });

        for (let i = 0; i < localBlobClips.length; i++) {
            const clip = localBlobClips[i];
            const pct = 5 + Math.round(((i + 1) / Math.max(1, localBlobClips.length)) * 5);
            onStatus?.(`Validation média local ${i + 1}/${localBlobClips.length}...`, pct);
            try {
                const sourceUrl =
                    (clip.url && clip.url.startsWith('blob:'))
                        ? clip.url
                        : getClipLocalSourceUrl(clip);
                if (!sourceUrl) {
                    throw new Error('Aucune source blob locale trouvée');
                }

                const response = await fetch(sourceUrl);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
            } catch (err: any) {
                throw new Error(`Média local inaccessible pour "${clip.name}" (${clip.id}): ${err?.message || 'erreur inconnue'}`);
            }
        }

        onStatus?.('Préparation de la timeline avant burn...', 12);

        const prepManager = new ExportManager({
            aspectRatio: exportAspectRatio,
            fps: 30,
            videoBitrate: 20_000_000,
            audioBitrate: 192_000,
            audioSampleRate: 48000,
            maxRetries: 1,
            strategy: 'concat',
            burnSubtitles: false,
            scaleMode,
            format: 'mp4',
            userId: user?.uid,
            projectId: currentProjectIdRef.current || undefined,
        });

        const preassembledBlob = await prepManager.export(
            clipsToAssemble,
            tracks,
            activeSubtitleLanguage,
            subtitleStyle,
            (progress) => {
                // Map prep progress in a small early window so job status remains readable.
                const mapped = 12 + Math.round((progress.progress || 0) * 0.35);
                onStatus?.(`Préparation timeline: ${progress.message}`, mapped);
            }
        );

        onStatus?.('Upload de la source intermédiaire...', 50);
        const prepFile = new File([preassembledBlob], `burn_source_${Date.now()}.mp4`, { type: 'video/mp4' });
        const sourceUrl = await uploadAssetToStorage(prepFile);
        if (!sourceUrl) {
            throw new Error("Echec de l'upload de la source intermediaire");
        }

        onStatus?.('Source intermédiaire prête', 60);
        return sourceUrl;
    }, [
        exportAspectRatio,
        scaleMode,
        user,
        currentProjectIdRef,
        tracks,
        activeSubtitleLanguage,
        subtitleStyle,
        uploadAssetToStorage
    ]);

    // ============ HANDLERS ============

    const handleStartExport = useCallback(async (config: {
        mode: string;
        format: string;
        compression: string;
        extension: string;
        codec: string;
        burnSubtitles?: boolean;
        strategy?: string;
    }) => {
        setIsExporting(true);
        setExportStatus('Initialisation...');
        setExportProgress(0);
        setExportDetails([]);
        setVfxSegmentPreviews([]);

        try {
            const exportConfig: any = {
                aspectRatio: exportAspectRatio,
                fps: 30,
                videoBitrate: 20_000_000,
                audioBitrate: 192_000,
                audioSampleRate: 48000,
                maxRetries: 2,
                strategy: config.strategy || 'auto',
                burnSubtitles: config.burnSubtitles || false,
                scaleMode: scaleMode,
                userId: user?.uid,
                projectId: currentProjectIdRef.current || undefined,
            };

            // Magnetize clips before export (snap to 0.1s for clean cuts)
            const magnetizedClips = allClips.map(clip => {
                const SNAP_THRESHOLD = 0.1;
                const roundToThreshold = (value: number) => Math.round(value / SNAP_THRESHOLD) * SNAP_THRESHOLD;

                return {
                    ...clip,
                    start: roundToThreshold(clip.start),
                    duration: roundToThreshold(clip.duration),
                    trimStart: roundToThreshold(clip.trimStart),
                };
            });

            // Create export manager
            exportManagerRef.current = new ExportManager({ ...exportConfig, format: 'mp4' });

            setExportStatus('Export en cours...');
            const mp4Blob = await exportManagerRef.current.export(
                magnetizedClips,
                tracks,
                activeSubtitleLanguage,
                subtitleStyle,
                (progress) => {
                    setExportStatus(`Export: ${progress.message}`);
                    setExportProgress(progress.progress);
                    if (progress.details) {
                        setExportDetails(progress.details);
                    }
                    if ((progress as any).segmentPreview) {
                        const preview = (progress as any).segmentPreview;
                        setVfxSegmentPreviews(prev => [...prev, preview]);
                    }
                    // Capture chunks for grid visualization (Smart Hybrid)
                    if (progress.chunks) {
                        setChunksIfChanged(progress.chunks);
                    }
                }
            );

            const urlMP4 = URL.createObjectURL(mp4Blob);
            const thumbnailUrl = await generateThumbnailFromUrl(urlMP4);
            const srtContent = generateSRT(magnetizedClips, activeSubtitleLanguage);

            const resolution = ASPECT_RATIOS[exportAspectRatio];
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const timeStr = `${String(now.getHours()).padStart(2, '0')}h${String(now.getMinutes()).padStart(2, '0')}`;
            const baseName = `${dateStr}_${timeStr}`;

            const videoId = Date.now().toString();
            const newVideo: ExportedVideoLocal = {
                id: videoId,
                name: baseName,
                urlWebM: undefined,
                urlMP4,
                thumbnailUrl: savedProjects.find(p => p.id === currentProjectIdRef.current)?.thumbnailUrl || thumbnailUrl,
                mp4ConversionProgress: undefined,
                srtContent: config.burnSubtitles ? undefined : (srtContent || undefined),
            };
            setExportedVideos(prev => [newVideo, ...prev]);

            setExportStatus('Upload vers R2...');
            setExportProgress(95);

            // Upload to R2 for persistence
            try {
                const file = new File([mp4Blob], `${baseName}.mp4`, { type: 'video/mp4' });
                const r2Url = await uploadAssetToStorage(file);
                if (r2Url) {
                    // Update the local state with the R2 URL
                    const persistedVideo = { ...newVideo, urlMP4: r2Url };
                    setExportedVideos(prev => prev.map(v => v.id === videoId ? persistedVideo : v));

                    // Save to Firestore
                    const projectId = currentProjectIdRef.current;
                    if (projectId && user) {
                        const { doc, updateDoc, arrayUnion, getFirestore } = await import('firebase/firestore');
                        const db = getFirestore();
                        const projectRef = doc(db, 'users', user.uid, 'projects', projectId);
                        const exportEntry = {
                            id: videoId,
                            name: baseName,
                            urlMP4: r2Url,
                            srtContent: persistedVideo.srtContent,
                        };
                        await updateDoc(projectRef, { exports: arrayUnion(exportEntry) });
                        console.log('[Export] Saved to R2 + Firestore ✓');
                    }
                }
            } catch (uploadErr) {
                console.warn('[Export] R2 upload failed (export still available as blob):', uploadErr);
            }

            setExportStatus('Export terminé !');
            setExportProgress(100);

            setTimeout(() => {
                setIsExporting(false);
                setExportStatus('');
                setExportProgress(0);
                exportManagerRef.current = null;
            }, 2000);

        } catch (error) {
            console.error('Export error:', error);
            const errorMessage = error instanceof ExportError
                ? error.message
                : 'Une erreur est survenue lors de l\'export';

            setExportStatus(`Erreur: ${errorMessage}`);
            setExportProgress(0);

            setTimeout(() => {
                setIsExporting(false);
                setExportStatus('');
                exportManagerRef.current = null;
            }, 5000);
        }
    }, [allClips, tracks, activeSubtitleLanguage, subtitleStyle, exportAspectRatio, scaleMode, savedProjects, currentProjectIdRef, setChunksIfChanged]);

    const handleCancelExport = useCallback(() => {
        if (exportManagerRef.current) {
            exportManagerRef.current.abort();
            exportManagerRef.current = null;
        }
        setIsExporting(false);
        setExportStatus('');
        setExportProgress(0);
    }, []);

    const handleStartServerExport = useCallback(async (options?: {
        burnSubtitles?: boolean;
        quality?: 'draft' | 'standard' | 'high';
        mode?: 'chunk' | 'full';
        useGPU?: boolean; // Force GPU export via RunPod
        engine?: 'mlt' | 'ffmpeg'; // Engine selection
    }) => {
        if (!user || !currentProjectId) {
            console.error('[ServerExport] No user or project ID');
            return;
        }

        const tempJobId = `pending_${Date.now()}`;
        const newExportState: ServerExportState = {
            isActive: true,
            jobId: tempJobId,
            projectId: currentProjectId,
            status: 'pending',
            progress: 0,
            currentStep: 'Envoi du job en cours...',
        };

        setActiveServerExports(prev => [newExportState, ...prev]);

        // Helper to update specific job status
        const updateJobStatus = (id: string, updates: Partial<ServerExportState>) => {
            setActiveServerExports(prev => prev.map(job =>
                job.jobId === id ? { ...job, ...updates } : job
            ));
        };

        // Helper to replace job ID (when switching from pending to real ID)
        const replaceJobId = (oldId: string, newId: string, updates: Partial<ServerExportState>) => {
            setActiveServerExports(prev => prev.map(job =>
                job.jobId === oldId ? { ...job, jobId: newId, ...updates } : job
            ));
        };

        try {
            const client = getServerExportClient(user.uid);

            // Auto-repair missing `clip.url` from videoVersions (legacy/project drift safety)
            let validClips = allClips.map(c => {
                if (c.url || !c.videoVersions || c.videoVersions.length === 0) return c;

                const preferredVersion =
                    (c.activeVideoVersion
                        ? c.videoVersions.find(v => v.id === c.activeVideoVersion)
                        : undefined) ||
                    c.videoVersions.find(v => v.id === 'original') ||
                    [...c.videoVersions].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0];

                if (!preferredVersion?.url) return c;
                return { ...c, url: preferredVersion.url };
            });

            // Auto-upload local blob URLs to R2
            const isMediaClip = (c: Clip) => c.type !== 'text' && c.type !== 'synthe' && c.type !== 'panel';
            const localClips = validClips.filter(c => {
                if (!isMediaClip(c)) return false;

                const localSource = getClipLocalSourceUrl(c);
                const hasLocalBlob = !!localSource && localSource.startsWith('blob:');
                const hasLocalFileFallback = !!(c as any)._file || !!(c as any)._cachedBlob;

                // Needs upload if it relies on a blob URL *or* has no remote URL at all, AND it lacks alternative remote keys
                const hasValidUrl = c.url && !c.url.startsWith('blob:') && c.url.startsWith('http');
                const hasAlternativeRemote = !!(c as any).r2Key || !!c.streamUrl || c.videoVersions?.some(v => v.url || (v as any).r2Key);
                const needsUpload = !hasValidUrl && !hasAlternativeRemote;

                return (hasLocalBlob || hasLocalFileFallback) && needsUpload;
            });

            if (localClips.length > 0) {
                console.log(`[ServerExport] Auto-uploading ${localClips.length} local clips...`);
                updateJobStatus(tempJobId, {
                    status: 'processing',
                    currentStep: `Synchronisation des médias (${localClips.length})...`
                });

                const updatedUrls = new Map<string, string>();

                for (let i = 0; i < localClips.length; i++) {
                    const clip = localClips[i];
                    updateJobStatus(tempJobId, { progress: Math.round((i / localClips.length) * 100) });

                    try {
                        let fileToUpload: File | null = null;

                        // 1) Prefer blob URL source (clip.url or clip.localUrl)
                        const sourceUrl =
                            (clip.url && clip.url.startsWith('blob:'))
                                ? clip.url
                                : getClipLocalSourceUrl(clip);
                        if (sourceUrl && sourceUrl.startsWith('blob:')) {
                            const response = await fetch(sourceUrl);
                            const blob = await response.blob();
                            fileToUpload = new File(
                                [blob],
                                `${clip.name.replace(/\.[^/.]+$/, "")}.mp4`,
                                { type: blob.type || 'video/mp4' }
                            );
                        }

                        // 2) Fallback to original dropped file stored on clip
                        if (!fileToUpload && (clip as any)._file instanceof File) {
                            fileToUpload = (clip as any)._file as File;
                        }

                        // 3) Fallback to cached blob stored on clip
                        if (!fileToUpload && (clip as any)._cachedBlob instanceof Blob) {
                            const cachedBlob = (clip as any)._cachedBlob as Blob;
                            fileToUpload = new File(
                                [cachedBlob],
                                `${clip.name.replace(/\.[^/.]+$/, "")}.mp4`,
                                { type: cachedBlob.type || 'video/mp4' }
                            );
                        }

                        if (!fileToUpload) {
                            throw new Error(`No local media source (_file/_cachedBlob/blob URL) for clip ${clip.id}`);
                        }

                        const publicUrl = await uploadAssetToStorage(fileToUpload, clip.id, undefined, currentProjectId);
                        if (publicUrl) {
                            updatedUrls.set(clip.id, publicUrl);
                        }
                    } catch (err) {
                        console.error(`[ServerExport] Failed to upload clip ${clip.id}`, err);
                    }
                }

                validClips = validClips.map(c => {
                    if (updatedUrls.has(c.id)) {
                        return { ...c, url: updatedUrls.get(c.id) };
                    }
                    return c;
                });

                updateJobStatus(tempJobId, { progress: 100, currentStep: 'Synchronisation terminée.' });
            }

            // SAFETY CHECK 1: Ensure no unresolved local blob remains as primary source
            const remainingBlobs = validClips.filter(c => {
                if (!isMediaClip(c)) return false;
                const localSource = getClipLocalSourceUrl(c);
                const hasLocalBlob = !!localSource && localSource.startsWith('blob:');
                const missingRemote = !c.url || c.url.startsWith('blob:');
                return hasLocalBlob && missingRemote;
            });
            if (remainingBlobs.length > 0) {
                const failMsg = `Échec de la synchronisation de ${remainingBlobs.length} médias. L'export serveur ne peut pas continuer avec des URLs locales (blob:).`;
                console.error(`[ServerExport] ${failMsg}`, remainingBlobs);
                updateJobStatus(tempJobId, {
                    status: 'failed',
                    currentStep: 'Erreur de synchronisation',
                    error: failMsg,
                    isActive: false
                });
                return; // ABORT EXPORT
            }

            // SAFETY CHECK 2: Ensure every media clip has at least one remote source for server-side processing.
            // Removed strict checks because backend export logic `getClipRemoteUrl` has robust fallbacks 
            // for resolving optimal urls from R2, legacy databases, or videoVersions.
            const missingRemoteSource = validClips.filter(c => {
                if (!isMediaClip(c)) return false;

                const isOnlyBlob = c.url && c.url.startsWith('blob:') && !c.streamUrl && !(c as any).r2Key && (!c.videoVersions || c.videoVersions.length === 0);
                const isCompletelyEmpty = !c.url && !c.streamUrl && !(c as any).r2Key && (!c.videoVersions || c.videoVersions.length === 0) && !c.localUrl;

                return isOnlyBlob || isCompletelyEmpty;
            });

            if (missingRemoteSource.length > 0) {
                const ids = missingRemoteSource.map(c => `${c.name || c.id} (${c.type})`).join(', ');
                const warnMsg = `Sources distantes potentiellement manquantes pour ${missingRemoteSource.length} média(s): ${ids}. L'export continue, le serveur tentera de les résoudre ou les ignorera.`;
                console.warn(`[ServerExport] ${warnMsg}`);
                console.warn('[ServerExport] Missing clip diagnostics:', missingRemoteSource.map(c => ({
                    id: c.id,
                    type: c.type,
                    hasUrl: !!c.url,
                    hasLocalUrl: !!c.localUrl,
                    hasStreamUrl: !!c.streamUrl,
                    hasR2Key: !!(c as any).r2Key,
                    hasFile: !!(c as any)._file,
                    hasCachedBlob: !!(c as any)._cachedBlob,
                    videoVersions: c.videoVersions?.length || 0,
                    activeVideoVersion: c.activeVideoVersion || null,
                })));
            }

            const totalDuration = Math.max(...validClips.map(c => c.start + c.duration), 0);
            const CHUNK_THRESHOLD = 600;
            const TIMELINE_CUT_CHUNK_THRESHOLD = 300;
            const timelineCutPoints = buildTimelineCutPoints(validClips, tracks);
            const shouldUseTimelineCutPoints = totalDuration > TIMELINE_CUT_CHUNK_THRESHOLD && timelineCutPoints.length > 2;

            // Check if timeline has heavy effects (overlays, synthe, panels, etc.)
            // If so, force chunking even for short videos
            const hasHeavyEffects = validClips.some(c =>
                ['overlay', 'synthe', 'panel', 'zoom', 'html', 'text', 'image'].includes(c.type)
            );

            const exportConfig = {
                projectId: currentProjectId,
                clips: validClips,
                tracks: tracks,
                scaleMode,
                activeSubtitleLanguage: activeSubtitleLanguage,
                subtitleStyle: subtitleStyle,
                quality: options?.quality || 'standard',
                resolution: ASPECT_RATIOS[exportAspectRatio],
                burnSubtitles: options?.burnSubtitles ?? false,
                engine: options?.engine,
                cutPoints: shouldUseTimelineCutPoints ? timelineCutPoints : undefined,
            };

            let jobId: string;
            const shouldUseChunking =
                shouldUseTimelineCutPoints ||
                options?.mode === 'chunk' ||
                (options?.engine !== 'mlt' &&
                    options?.mode !== 'full' &&
                    (totalDuration > CHUNK_THRESHOLD || hasHeavyEffects));

            // ============ BURN PARALLEL + CLIENT-SIDE MERGE PATH ============
            // Default export path: cloud encodes chunks in parallel,
            // client downloads from R2 CDN + merges with FFmpeg.wasm (no server merge wait)
            {
                const hasSubtitles = validClips.some(c =>
                    !!c.subtitles?.[activeSubtitleLanguage]?.some(sub => !sub.hidden)
                );
                const srtContent = hasSubtitles
                    ? generateSRTFromClips(validClips, activeSubtitleLanguage)
                    : '';

                // Burn-parallel expects a single flat source video + SRT.
                // For montage correctness, always prepare a timeline-assembled source first.
                if (srtContent && options?.engine !== 'mlt' && !hasHeavyEffects) {
                    console.log(`[ServerExport] 🔥 Burn-parallel + client merge (${Math.round(totalDuration)}s)`);
                    updateJobStatus(tempJobId, {
                        status: 'processing',
                        currentStep: 'Préparation timeline + export parallèle...',
                    });

                    // Reset chunk state
                    setChunks([]);
                    setIsMerging(false);

                    try {
                        const timelineSourceUrl = await prepareTimelineSourceForBurnParallel(
                            validClips,
                            (step, progress) => {
                                updateJobStatus(tempJobId, {
                                    progress,
                                    currentStep: step,
                                });
                            }
                        );

                        const { jobId: burnJobId, totalChunks } = await client.submitBurnParallelWithChunks(
                            timelineSourceUrl,
                            srtContent,
                            currentProjectId,
                            // onChunkUpdate: update chunk state + auto-download completed chunks
                            (chunkIndex, serverStatus, serverProgress, chunkUrl) => {
                                const chunkUpdates: Partial<ChunkStatus> = {
                                    serverStatus: serverStatus as ChunkStatus['serverStatus'],
                                    serverProgress,
                                };
                                if (chunkUrl) chunkUpdates.url = chunkUrl;
                                upsertChunkAtIndex(chunkIndex, chunkUpdates, 'server');

                                // Auto-download when chunk becomes server-complete
                                if (serverStatus === 'complete' && chunkUrl && !isChunkDownloading(chunkIndex)) {
                                    const current = chunksRef.current[chunkIndex];
                                    if (!current || current.localStatus === 'waiting' || current.localStatus === 'failed') {
                                        downloadChunkToFFmpegFS(chunkIndex, chunkUrl, updateChunkAtIndex).catch(err => {
                                            console.warn(`[ClientMerge] Chunk ${chunkIndex} download failed:`, err);
                                        });
                                    }
                                }
                            },
                            // onProgress: update job status
                            (step, progress) => {
                                updateJobStatus(tempJobId, {
                                    progress,
                                    currentStep: `🔥 ${step}`,
                                });
                            },
                            exportConfig.subtitleStyle,
                            exportConfig.cutPoints
                        );

                        // All chunks server-complete — replace temp ID with burn job ID
                        replaceJobId(tempJobId, burnJobId, {
                            status: 'processing',
                            currentStep: '⚙️ Téléchargement des chunks...',
                            progress: 80,
                        });

                        // The useEffect above will auto-trigger merge when all chunks are locally downloaded
                        return; // Done (async merge will happen via useEffect)
                    } catch (burnError: any) {
                        console.warn('[ServerExport] Burn-parallel + client merge failed, falling back:', burnError.message);
                        setChunks([]);
                        // Fall through to GPU/CPU paths
                    }
                }
            }

            // ============ GPU EXPORT PATH (RunPod) — Only when explicitly requested ============
            const shouldUseGPU = options?.useGPU === true;
            const gpuClient = getRunPodClient();

            if (shouldUseGPU && gpuClient) {
                console.log(`[ServerExport] 🚀 GPU mode via RunPod (Duration: ${Math.round(totalDuration)}s)`);
                updateJobStatus(tempJobId, {
                    status: 'processing',
                    currentStep: 'Export GPU en cours (RunPod)...',
                });

                try {
                    const gpuJobId = `gpu_${currentProjectId}_${Date.now()}`;

                    replaceJobId(tempJobId, gpuJobId, {});
                    jobId = gpuJobId;

                    // Use submitExportSync which routes through Firebase Proxy (bypasses CORS)
                    const result = await gpuClient.submitExportSync({
                        jobId: gpuJobId,
                        projectId: currentProjectId,
                        userId: user.uid,
                        clips: validClips,
                        tracks: tracks,
                        activeSubtitleLanguage: activeSubtitleLanguage,
                        subtitleStyle: subtitleStyle,
                        burnSubtitles: options?.burnSubtitles ?? false,
                        quality: options?.quality || 'standard',
                        resolution: ASPECT_RATIOS[exportAspectRatio],
                    });

                    if (result.status === 'completed' && result.downloadUrl) {
                        console.log(`[ServerExport] ⚡ GPU export complete in ${result.processingTime}ms`);
                        console.log(`[ServerExport] Encoder used: ${result.encoder} (${result.encoderType})`);

                        updateJobStatus(gpuJobId, {
                            status: 'complete',
                            progress: 100,
                            currentStep: 'Export GPU terminé!',
                            downloadUrl: result.downloadUrl,
                        });
                        return;
                    } else {
                        throw new Error(result.error || 'GPU export failed');
                    }
                } catch (gpuError: any) {
                    console.warn('[ServerExport] GPU export failed, falling back to CPU:', gpuError.message);
                    // Fall through to CPU export
                }
            }

            // ============ CPU EXPORT PATH (Cloud Run) ============
            if (shouldUseChunking) {
                console.log(`[ServerExport] CHUNK mode (Duration: ${Math.round(totalDuration)}s)`);
                updateJobStatus(tempJobId, {
                    status: 'processing',
                    currentStep: 'Export parallèle en cours...',
                });

                jobId = await client.submitChunkedExport(exportConfig, (chunksDone, totalChunks, phase) => {
                    const progress = phase === 'merging' ? 90 : Math.round((chunksDone / totalChunks) * 85);

                    setActiveServerExports(prev => prev.map(job => {
                        // Match either temp or real ID (if we updated it)
                        if (job.jobId === tempJobId || (jobId && job.jobId === jobId)) {
                            return {
                                ...job,
                                progress,
                                currentStep: phase === 'merging' ? 'Fusion des segments...' : `Chunk ${chunksDone}/${totalChunks}...`,
                            };
                        }
                        return job;
                    }));
                });

                replaceJobId(tempJobId, jobId, {});
            } else {
                console.log(`[ServerExport] FULL mode (Single Pass)`);
                const targetProjectId = currentProjectId;

                const subscription = client.submitAndSubscribe(exportConfig, (status) => {
                    console.log('[ServerExport] Status update:', status);

                    setActiveServerExports(prev => {
                        return prev.map(j => {
                            if (j.jobId === status.jobId || j.jobId === tempJobId) {
                                return {
                                    ...j,
                                    jobId: status.jobId, // Ensure ID is sync
                                    status: status.status,
                                    progress: status.progress,
                                    currentStep: status.currentStep,
                                    downloadUrl: status.downloadUrl,
                                    error: status.error,
                                    isActive: status.status !== 'failed'
                                };
                            }
                            return j;
                        });
                    });

                    if (currentProjectIdRef.current !== targetProjectId) {
                        console.log(`[ServerExport] Ignoring update for ${targetProjectId}`);
                        if (status.status === 'complete' && status.downloadUrl) {
                            const exportName = `Cloud_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', 'h')}_${status.jobId.slice(-6)}`;
                            const newExport = { id: status.jobId, name: exportName, urlMP4: status.downloadUrl };
                            import('firebase/firestore').then(({ doc, updateDoc, arrayUnion, getFirestore }) => {
                                const db = getFirestore();
                                const projectRef = doc(db, 'users', user.uid, 'projects', targetProjectId);
                                updateDoc(projectRef, { exports: arrayUnion(newExport) }).catch(console.warn);
                            });
                            // Cleanup
                            if (serverExportUnsubscribesRef.current.has(status.jobId)) {
                                serverExportUnsubscribesRef.current.get(status.jobId)?.();
                                serverExportUnsubscribesRef.current.delete(status.jobId);
                            }
                        }
                        return;
                    }

                    if (status.status === 'complete' && status.downloadUrl) {
                        const exportName = `Cloud_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', 'h')}_${status.jobId.slice(-6)}`;
                        const newExport = {
                            id: status.jobId,
                            name: exportName,
                            urlMP4: status.downloadUrl,
                        };

                        setExportedVideos(prev => {
                            if (prev.some(v => v.id === status.jobId)) return prev;
                            const updated = [...prev, newExport];

                            if (targetProjectId && user) {
                                import('firebase/firestore').then(({ doc, updateDoc, arrayUnion, getFirestore }) => {
                                    const db = getFirestore();
                                    const projectRef = doc(db, 'users', user.uid, 'projects', targetProjectId);
                                    updateDoc(projectRef, { exports: arrayUnion(newExport) }).catch(console.warn);
                                });
                            }

                            return updated;
                        });

                        if (status.status === 'complete' || status.status === 'failed') {
                            if (serverExportUnsubscribesRef.current.has(status.jobId)) {
                                serverExportUnsubscribesRef.current.get(status.jobId)?.();
                                serverExportUnsubscribesRef.current.delete(status.jobId);
                            }
                        }
                    }
                });

                jobId = subscription.jobId;

                // Store unsubscribe
                if (subscription.unsubscribe) {
                    serverExportUnsubscribesRef.current.set(jobId, subscription.unsubscribe);
                }

                replaceJobId(tempJobId, jobId, {});
            }

            // Final check/update
            updateJobStatus(jobId!, {
                // status: 'pending', // Don't overwrite if sub already updated
                // currentStep: 'Traitement en cours...'
            });

        } catch (error) {
            console.error('[ServerExport] Failed:', error);
            updateJobStatus(tempJobId, {
                isActive: false,
                error: String(error),
                status: 'failed'
            });
        }
    }, [
        user,
        currentProjectId,
        currentProjectIdRef,
        allClips,
        tracks,
        activeSubtitleLanguage,
        subtitleStyle,
        exportAspectRatio,
        scaleMode,
        prepareTimelineSourceForBurnParallel
    ]);

    const handleCancelServerExport = useCallback(async (jobId?: string) => {
        // If no ID provided, cancel all? Or cancel the first active?
        // Let's assume ID is provided for the new UI. 
        // If not, we fall back to cancelling all active ones.

        const jobsToCancel = jobId
            ? activeServerExports.filter(j => j.jobId === jobId)
            : activeServerExports;

        for (const job of jobsToCancel) {
            if (!job.jobId) continue;

            // Unsubscribe listener
            if (serverExportUnsubscribesRef.current.has(job.jobId)) {
                serverExportUnsubscribesRef.current.get(job.jobId)?.();
                serverExportUnsubscribesRef.current.delete(job.jobId);
            }

            // Update state
            setActiveServerExports(prev => prev.filter(j => j.jobId !== job.jobId)); // Remove from list or mark cancelled?
            // User probably wants it removed from the "Active" list.

            try {
                const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';
                const response = await fetch(`${EXPORT_SERVICE_URL}/cancel/${job.jobId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (response.ok) {
                    console.log(`[ServerExport] Job ${job.jobId} cancelled`);
                } else {
                    console.warn(`[ServerExport] Failed to cancel job ${job.jobId}`);
                }
            } catch (error) {
                console.error('[ServerExport] Cancel error:', error);
            }
        }
    }, [activeServerExports]);

    const handleDeleteExport = useCallback(async (videoId: string, videoUrl: string) => {
        if (!user || !currentProjectId) return;

        console.log('[Export] Deleting:', videoId);
        setExportedVideos(prev => prev.filter(v => v.id !== videoId));

        try {
            const { doc, updateDoc, getFirestore, getDoc } = await import('firebase/firestore');
            const db = getFirestore();
            const projectRef = doc(db, 'users', user.uid, 'projects', currentProjectId);

            const projectSnap = await getDoc(projectRef);
            if (projectSnap.exists()) {
                const projectData = projectSnap.data();
                const updatedExports = (projectData.exports || []).filter((e: any) => e.id !== videoId);
                await updateDoc(projectRef, { exports: updatedExports });
                console.log('[Export] Removed from Firestore');
            }
        } catch (err) {
            console.warn('[Export] Firestore remove failed:', err);
        }
    }, [user, currentProjectId]);

    const checkActiveExportJobs = useCallback(async (projectId: string) => {
        if (!user) return;

        try {
            const { collection, query, where, getDocs, onSnapshot, doc, getFirestore } = await import('firebase/firestore');
            const db = getFirestore();

            const jobsQuery = query(
                collection(db, 'export-jobs'),
                where('projectId', '==', projectId),
                where('status', 'in', ['queued', 'analyzing', 'pending', 'downloading', 'processing', 'encoding', 'uploading'])
            );

            const snapshot = await getDocs(jobsQuery);

            if (snapshot.empty) {
                console.log('[ExportPersistence] No active jobs for', projectId);
                return;
            }

            console.log(`[ExportPersistence] Found ${snapshot.docs.length} active job(s)`);

            // Merge found jobs into state, avoiding duplicates
            const newJobs: ServerExportState[] = [];

            snapshot.docs.forEach(jobDoc => {
                const jobData = jobDoc.data();
                const jobId = jobDoc.id;
                const targetProjectId = projectId;

                const jobState: ServerExportState = {
                    isActive: true,
                    jobId,
                    projectId,
                    status: jobData.status,
                    progress: jobData.progress || 0,
                    currentStep: jobData.currentStep || 'En cours...',
                };

                const totalChunks = jobData.totalChunks || 0;
                if (totalChunks > 0) {
                    const mappedChunks: ChunkStatus[] = [];
                    for (let ci = 0; ci < totalChunks; ci++) {
                        mappedChunks.push({
                            index: ci,
                            serverStatus: (jobData[`chunk_${ci}_status`] || 'pending') as any,
                            serverProgress: jobData[`chunk_${ci}_progress`] || 0,
                            url: jobData[`chunk_${ci}_url`] || null,
                            localStatus: jobData[`chunk_${ci}_status`] === 'complete' ? 'downloaded' : 'waiting',
                            localProgress: jobData[`chunk_${ci}_status`] === 'complete' ? 100 : 0,
                            size: 0
                        });
                    }
                    jobState.chunks = mappedChunks;
                }

                newJobs.push(jobState);

                // Set up listener if not already listening
                if (!serverExportUnsubscribesRef.current.has(jobId)) {
                    const unsub = onSnapshot(doc(db, 'export-jobs', jobId), (docSnap) => {
                        const status = docSnap.data() as ExportJobStatus | undefined;
                        if (!status) return;

                        // Update State
                        setActiveServerExports(prev => prev.map(j => {
                            if (j.jobId === jobId) {
                                let updatedChunks = j.chunks;
                                const totalChunks = status.totalChunks || 0;
                                if (totalChunks > 0) {
                                    updatedChunks = [];
                                    for (let ci = 0; ci < totalChunks; ci++) {
                                        updatedChunks.push({
                                            index: ci,
                                            serverStatus: ((status as any)[`chunk_${ci}_status`] || 'pending') as any,
                                            serverProgress: (status as any)[`chunk_${ci}_progress`] || 0,
                                            url: (status as any)[`chunk_${ci}_url`] || null,
                                            localStatus: (status as any)[`chunk_${ci}_status`] === 'complete' ? 'downloaded' : 'waiting',
                                            localProgress: (status as any)[`chunk_${ci}_status`] === 'complete' ? 100 : 0,
                                            size: 0
                                        });
                                    }
                                }

                                return {
                                    ...j,
                                    status: status.status,
                                    progress: status.progress || 0,
                                    currentStep: status.currentStep,
                                    downloadUrl: status.downloadUrl,
                                    error: status.error,
                                    isActive: status.status !== 'failed',
                                    chunks: updatedChunks
                                };
                            }
                            return j;
                        }));

                        if (currentProjectIdRef.current !== targetProjectId) {
                            if (status.status === 'complete' && status.downloadUrl) {
                                const exportName = `Cloud_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', 'h')}_${jobId.slice(-6)}`;
                                const newExport = { id: jobId, name: exportName, urlMP4: status.downloadUrl };
                                import('firebase/firestore').then(({ doc, updateDoc, arrayUnion, getFirestore }) => {
                                    const db = getFirestore();
                                    const projectRef = doc(db, 'users', user.uid, 'projects', targetProjectId);
                                    updateDoc(projectRef, { exports: arrayUnion(newExport) }).catch(console.warn);
                                });
                                // Cleanup
                                if (serverExportUnsubscribesRef.current.has(jobId)) {
                                    serverExportUnsubscribesRef.current.get(jobId)?.();
                                    serverExportUnsubscribesRef.current.delete(jobId);
                                }
                            }
                            return;
                        }

                        if (status.status === 'complete' && status.downloadUrl) {
                            const exportName = `Cloud_${new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', 'h')}_${jobId.slice(-6)}`;
                            const projectThumb = savedProjects.find(p => p.id === targetProjectId)?.thumbnailUrl;
                            const newExport = projectThumb
                                ? {
                                    id: jobId,
                                    name: exportName,
                                    urlMP4: status.downloadUrl,
                                    thumbnailUrl: projectThumb
                                }
                                : {
                                    id: jobId,
                                    name: exportName,
                                    urlMP4: status.downloadUrl,
                                };

                            setExportedVideos(prev => {
                                if (prev.some(v => v.id === jobId)) return prev;
                                const updated = [...prev, newExport];

                                if (targetProjectId && user) {
                                    import('firebase/firestore').then(({ doc, updateDoc, arrayUnion, getFirestore }) => {
                                        const db = getFirestore();
                                        const projectRef = doc(db, 'users', user.uid, 'projects', targetProjectId);
                                        updateDoc(projectRef, { exports: arrayUnion(newExport) }).catch(console.warn);
                                    });
                                }

                                return updated;
                            });
                            // Cleanup
                            if (serverExportUnsubscribesRef.current.has(jobId)) {
                                serverExportUnsubscribesRef.current.get(jobId)?.();
                                serverExportUnsubscribesRef.current.delete(jobId);
                            }
                        }

                        if (status.status === 'failed') {
                            if (serverExportUnsubscribesRef.current.has(jobId)) {
                                serverExportUnsubscribesRef.current.get(jobId)?.();
                                serverExportUnsubscribesRef.current.delete(jobId);
                            }
                        }
                    });

                    serverExportUnsubscribesRef.current.set(jobId, unsub);
                }
            });

            // Merge valid new jobs into state (simple distinct merge)
            setActiveServerExports(prev => {
                const existingIds = new Set(prev.map(j => j.jobId));
                const uniqueNewJobs = newJobs.filter(j => j.jobId && !existingIds.has(j.jobId));
                return [...uniqueNewJobs, ...prev];
            });

        } catch (error) {
            console.error('[ExportPersistence] Failed:', error);
        }
    }, [user, currentProjectIdRef, savedProjects]);

    // Computed: per-project export status for sidebar badges
    const exportStatusByProject = useMemo(() => {
        const map: Record<string, { status: 'exporting' | 'uploading' | 'complete' | 'failed'; progress: number; step?: string }> = {};

        // Local export (current project)
        if (isExporting && currentProjectId) {
            map[currentProjectId] = {
                status: exportStatus.includes('Erreur') ? 'failed' : exportStatus.includes('Upload') ? 'uploading' : 'exporting',
                progress: exportProgress,
                step: exportStatus,
            };
        }

        // Server exports (may belong to different projects)
        for (const job of activeServerExports) {
            if (!job.projectId || !job.isActive) continue;
            const s = job.status;
            map[job.projectId] = {
                status: s === 'failed' ? 'failed' : s === 'complete' ? 'complete' : 'exporting',
                progress: job.progress || 0,
                step: job.currentStep,
            };
        }

        return map;
    }, [isExporting, currentProjectId, exportProgress, exportStatus, activeServerExports]);

    // Retry a failed chunk download (with auto-retry support)
    const chunkRetryCountRef = useRef<Record<number, number>>({});
    const MAX_CLIENT_RETRIES = 3;

    // Core chunk processing logic extracted for reusability (hybrid mode)
    const processServerChunk = useCallback(async (
        chunkIndex: number, client: ReturnType<typeof getServerExportClient>, projectId: string,
        chunkClips: Clip[], tracks: Track[], activeSubtitleLanguage: string, subtitleStyle: any,
        exportAspectRatio: AspectRatio, updateChunk: (index: number, updates: Partial<ChunkStatus>) => void,
        totalChunks: number, chunkStart: number, chunkEnd: number, baseJobId: string, userId: string
    ) => {
        updateChunk(chunkIndex, { serverStatus: 'encoding', serverProgress: 0 });
        try {
            if (!userId) {
                throw new Error('Utilisateur non authentifié pour le chunk cloud');
            }

            // Use fire-and-forget submitSingleChunkJob (same pattern as normal export).
            // submitExportJob blocks until the server finishes (minutes) — wrong for hybrid.
            const chunkJobIdStr = `hybrid_${baseJobId}_chunk${chunkIndex}`;
            const { chunkJobId, fetchPromise } = await client.submitSingleChunkJob(
                chunkJobIdStr,
                {
                    projectId, clips: chunkClips, tracks, activeSubtitleLanguage, subtitleStyle,
                    quality: 'standard', burnSubtitles: true, resolution: ASPECT_RATIOS[exportAspectRatio],
                },
                chunkIndex,
                totalChunks,
                chunkStart,
                chunkEnd,
                `LVMH/outputs/${userId}/hybrid/${baseJobId}_chunk${chunkIndex}.mp4`
            );

            // Track fetch promise to detect silent network failures (e.g. browser connection limits)
            let fetchFailed = false;
            let fetchError: any = null;
            fetchPromise.catch(err => {
                fetchFailed = true;
                fetchError = err;
            });

            // Poll Firestore for completion
            let url: string | null = null;
            const startTime = Date.now();
            const chunkDurationSeconds = Math.max(1, chunkEnd - chunkStart);
            const MAX_POLL_TIME_MS = Math.max(
                45 * 60 * 1000,
                Math.ceil(chunkDurationSeconds) * 1000 * 6
            );

            while (!url) {
                if (fetchFailed) {
                    throw new Error(`Erreur réseau (Cloud Run inatteignable): ${fetchError?.message || 'Unknown error'}`);
                }
                if (Date.now() - startTime > MAX_POLL_TIME_MS) {
                    throw new Error(`Timeout: Le traitement du chunk a pris trop de temps.`);
                }

                await new Promise(r => setTimeout(r, 3000));
                const status = await client.getJobStatus(chunkJobId);
                if (status?.progress) updateChunk(chunkIndex, { serverProgress: status.progress });
                if (status?.status === 'complete' && status.downloadUrl) {
                    url = status.downloadUrl;
                } else if (status?.status === 'failed') {
                    throw new Error(status.error || `Chunk ${chunkIndex} failed`);
                }
            }

            updateChunk(chunkIndex, { serverStatus: 'complete', serverProgress: 100, url, localStatus: 'downloaded', localProgress: 100 });

        } catch (err: any) {
            console.error(`[HybridExport] ☁ Chunk #${chunkIndex} failed:`, err);
            updateChunk(chunkIndex, { serverStatus: 'failed', localStatus: 'failed' });
            throw err;
        }
    }, []);

    const processLocalChunk = useCallback(async (
        chunkIndex: number, chunkClips: Clip[], tracks: Track[],
        activeSubtitleLanguage: string, subtitleStyle: any, exportAspectRatio: AspectRatio,
        updateChunk: (index: number, updates: Partial<ChunkStatus>) => void
    ) => {
        updateChunk(chunkIndex, { localStatus: 'downloading', serverStatus: 'encoding' });
        try {
            const manager = new ExportManager({
                aspectRatio: exportAspectRatio, fps: 30, videoBitrate: 10_000_000,
                audioBitrate: 192_000, audioSampleRate: 48000, format: 'mp4',
                burnSubtitles: true, strategy: 'webcodecs',
            });

            const blob = await manager.export(
                chunkClips, tracks, activeSubtitleLanguage, subtitleStyle,
                (p) => {
                    updateChunk(chunkIndex, { localProgress: Math.round(p.progress), serverProgress: Math.round(p.progress) });
                }
            );

            updateChunk(chunkIndex, {
                localStatus: 'downloaded', localProgress: 100,
                serverStatus: 'complete', serverProgress: 100,
                size: blob.size,
                blob: blob,
            });
            console.log(`[HybridExport] ⚡ Chunk #${chunkIndex} done (${(blob.size / 1024 / 1024).toFixed(1)} MB)`);

        } catch (err: any) {
            console.error(`[HybridExport] ⚡ Chunk #${chunkIndex} failed:`, err);
            updateChunk(chunkIndex, { localStatus: 'failed', serverStatus: 'failed' });
            throw err;
        }
    }, []);

    // Store the precise clips for each chunk so we can retry local ones
    const hybridChunkPropsRef = useRef<Record<number, { mode: 'local' | 'server', clips: Clip[], start: number, end: number, total: number, baseJobId: string }>>({});

    const retryChunkDownload = useCallback((chunkIndex: number, isAuto = false) => {
        const chunk = chunksRef.current[chunkIndex];
        if (!chunk) return;

        const updateChunk = updateChunkAtIndex;

        const props = hybridChunkPropsRef.current[chunkIndex];

        if (chunk.mode === 'local' && props) {
            // Local Retry
            if (isChunkDownloading(chunkIndex)) return;
            chunkRetryCountRef.current[chunkIndex] = isAuto ? (chunkRetryCountRef.current[chunkIndex] || 0) + 1 : 0;

            processLocalChunk(chunkIndex, props.clips, tracks, activeSubtitleLanguage, subtitleStyle, exportAspectRatio, updateChunk)
                .catch(err => console.warn(`[ClientMerge] Web chunk ${chunkIndex} failed retry:`, err));
            return;
        }

        if (chunk.mode === 'server' && chunk.url) {
            // Server Retry (Download Phase - existing logic)
            if (isChunkDownloading(chunkIndex)) return; // Already in progress

            const retryNum = chunkRetryCountRef.current[chunkIndex] || 0;
            if (isAuto && retryNum >= MAX_CLIENT_RETRIES) {
                console.warn(`[ClientMerge] Chunk ${chunkIndex} download failed after ${MAX_CLIENT_RETRIES} auto-retries`);
                return;
            }

            if (isAuto) {
                chunkRetryCountRef.current[chunkIndex] = retryNum + 1;
                console.log(`[ClientMerge] Auto-retry chunk dl ${chunkIndex} (${retryNum + 1}/${MAX_CLIENT_RETRIES})`);
            } else {
                chunkRetryCountRef.current[chunkIndex] = 0; // Manual retry resets counter
            }

            // Reset chunk status before retry
            updateChunkAtIndex(chunkIndex, { localStatus: 'downloading', localProgress: 0 });

            downloadChunkToFFmpegFS(chunkIndex, chunk.url, updateChunkAtIndex).catch(err => {
                console.warn(`[ClientMerge] Chunk ${chunkIndex} download failed:`, err);
                const nextRetry = (chunkRetryCountRef.current[chunkIndex] || 0);
                if (nextRetry < MAX_CLIENT_RETRIES) {
                    const delay = Math.pow(2, nextRetry) * 2000;
                    setTimeout(() => retryChunkDownload(chunkIndex, true), delay);
                }
            });
            return;
        }

        if (chunk.mode === 'server' && !chunk.url && props && user && currentProjectIdRef.current) {
            // Server Retry (Full Encoding Phase)
            if (isChunkDownloading(chunkIndex)) return;
            chunkRetryCountRef.current[chunkIndex] = 0; // Manual Reset
            const client = getServerExportClient(user.uid);
            processServerChunk(chunkIndex, client, currentProjectIdRef.current, props.clips, tracks, activeSubtitleLanguage, subtitleStyle, exportAspectRatio, updateChunk, props.total, props.start, props.end, props.baseJobId, user.uid)
                .catch(err => console.warn(`[ClientMerge] Cloud chunk ${chunkIndex} failed full retry:`, err));
            return;
        }

    }, [tracks, activeSubtitleLanguage, subtitleStyle, exportAspectRatio, user, processLocalChunk, processServerChunk, updateChunkAtIndex]);

    // ============== HYBRID EXPORT (3-TIER) ==============
    // Mode 1 — 🟢 Full Web:   < 5 min → WebCodecs local (no server)
    // Mode 2 — 🟣 Constance:  ≥ 5 min, no FX → submitBurnParallelWithChunks
    // Mode 3 — 🔶 True Hybrid: ≥ 5 min, has FX → cut-based chunks (Web + Cloud parallel)
    const handleStartHybridExport = useCallback(async () => {
        if (!user || !currentProjectIdRef.current) return;
        setIsExporting(true);
        setHybridMode(true);

        const projectId = currentProjectIdRef.current;
        const client = getServerExportClient(user.uid);

        // ── Detect duration + effects ──
        const totalDuration = Math.max(...allClips.map(c => c.start + c.duration), 0);
        const { hasOverlays } = detectOverlayClips(allClips, tracks);
        const FULL_WEB_THRESHOLD = 300; // 5 min
        const FULL_WEB_COMPLEXITY_THRESHOLD = 120; // 2 min for complex timelines

        // Count actual video clips on track (cuts = multiple clips from montage)
        const videoClipCount = allClips.filter(c => c.type === 'video' && !c.clipRole).length;

        // < 5 min → full-web (one WebCodecs pass)
        // ≥ 5 min, multi-clip montage or has FX → true-hybrid
        // ≥ 5 min, single clip, no FX → constance
        const isComplexTimeline = hasOverlays || videoClipCount > 1;
        const useFullWeb = isComplexTimeline
            ? totalDuration < FULL_WEB_COMPLEXITY_THRESHOLD
            : totalDuration < FULL_WEB_THRESHOLD;

        const mode = useFullWeb
            ? 'full-web'
            : isComplexTimeline
                ? 'true-hybrid'
                : 'constance';

        console.log(`[HybridExport] Mode: ${mode} | Duration: ${Math.round(totalDuration)}s | Clips: ${videoClipCount} | HasFX: ${hasOverlays}`);

        const tempJobId = `hybrid_${projectId}_${Date.now()}`;
        const modeLabels = {
            'full-web': '🟢 Full Web — Export local WebCodecs',
            'constance': '🟣 Constance — Burn serveur parallèle',
            'true-hybrid': '🔶 Hybride — Web + Cloud en parallèle',
        };
        setActiveServerExports(prev => [...prev, {
            isActive: true,
            jobId: tempJobId,
            status: 'processing',
            progress: 0,
            currentStep: modeLabels[mode],
        }]);

        const updateJob = (updates: Partial<{ status: string; progress: number; currentStep: string; downloadUrl: string }>) => {
            setActiveServerExports(prev => prev.map(j =>
                j.jobId === tempJobId ? { ...j, ...updates } : j
            ));
        };
        const validateExportBlob = async (blob: Blob) => {
            // Guard against corrupted/empty outputs that still produce a downloadable file.
            if (!blob || blob.size < 64 * 1024) {
                throw new Error('Export invalide: fichier trop petit ou vide.');
            }
            const header = new Uint8Array(await blob.slice(4, 8).arrayBuffer());
            const signature = String.fromCharCode(...header);
            if (signature !== 'ftyp') {
                throw new Error('Export invalide: en-tête MP4 non reconnu.');
            }
        };

        let remoteWorkStarted = false;

        try {
            let finalBlob: Blob;

            // ━━━━━━━━━ MODE 1: FULL WEB (< 5 min) ━━━━━━━━━
            if (mode === 'full-web') {
                console.log(`[HybridExport] 🟢 Full Web — ExportManager with webcodecs`);
                updateJob({ currentStep: '🟢 Export local WebCodecs...' });

                const manager = new ExportManager({
                    aspectRatio: exportAspectRatio,
                    fps: 30,
                    videoBitrate: 20_000_000,
                    audioBitrate: 192_000,
                    audioSampleRate: 48000,
                    format: 'mp4',
                    burnSubtitles: true,
                    strategy: 'webcodecs',
                    scaleMode,
                });

                finalBlob = await manager.export(
                    allClips,
                    tracks,
                    activeSubtitleLanguage,
                    subtitleStyle,
                    (p) => {
                        updateJob({
                            progress: Math.round(p.progress * 0.95),
                            currentStep: `🟢 ${p.message || 'Export en cours...'}`,
                        });
                    }
                );

                console.log(`[HybridExport] 🟢 Full Web done: ${(finalBlob.size / 1024 / 1024).toFixed(1)} MB`);

                // ━━━━━━━━━ MODE 2: CONSTANCE (≥ 5 min, no FX) ━━━━━━━━━
            } else if (mode === 'constance') {
                const hasSubtitles = allClips.some(c =>
                    !!c.subtitles?.[activeSubtitleLanguage]?.some(sub => !sub.hidden)
                );
                const srtContent = hasSubtitles
                    ? generateSRTFromClips(allClips, activeSubtitleLanguage)
                    : '';
                const timelineCutPoints = buildTimelineCutPoints(allClips, tracks);

                if (!srtContent) {
                    throw new Error('Aucun sous-titre disponible pour le burn parallèle');
                }

                console.log(`[HybridExport] 🟣 Constance — submitBurnParallelWithChunks`);

                setChunks([]);
                setIsMerging(false);

                const timelineSourceUrl = await prepareTimelineSourceForBurnParallel(
                    allClips,
                    (step, progress) => {
                        updateJob({ currentStep: `🟣 ${step}`, progress });
                    }
                );

                const { jobId: burnJobId, totalChunks } = await client.submitBurnParallelWithChunks(
                    timelineSourceUrl,
                    srtContent,
                    projectId,
                    (chunkIndex, serverStatus, serverProgress, chunkUrl) => {
                        const chunkUpdates: Partial<ChunkStatus> = {
                            serverStatus: serverStatus as ChunkStatus['serverStatus'],
                            serverProgress,
                        };
                        if (chunkUrl) chunkUpdates.url = chunkUrl;
                        upsertChunkAtIndex(chunkIndex, chunkUpdates, 'server');

                        if (serverStatus === 'complete' && chunkUrl && !isChunkDownloading(chunkIndex)) {
                            const current = chunksRef.current[chunkIndex];
                            if (!current || current.localStatus === 'waiting' || current.localStatus === 'failed') {
                                downloadChunkToFFmpegFS(chunkIndex, chunkUrl, updateChunkAtIndex).catch(err => {
                                    console.warn(`[HybridExport] Chunk ${chunkIndex} download failed:`, err);
                                });
                            }
                        }
                    },
                    (step, progress) => {
                        updateJob({ progress, currentStep: `🟣 ${step}` });
                    },
                    subtitleStyle || null,
                    timelineCutPoints.length > 2 ? timelineCutPoints : undefined
                );
                remoteWorkStarted = true;

                // Wait for all downloads
                await new Promise<void>((resolve, reject) => {
                    const check = () => {
                        const current = chunksRef.current;
                        if (current.length === 0) { setTimeout(check, 500); return; }
                        if (current.every(c => c.localStatus === 'downloaded')) resolve();
                        else if (current.some(c => c.localStatus === 'failed')) reject(new Error('Chunk download failed'));
                        else setTimeout(check, 500);
                    };
                    check();
                });

                updateJob({ currentStep: '🔗 Assemblage local...', progress: 90 });
                setIsMerging(true);
                finalBlob = await mergeChunksWithFFmpeg(totalChunks);
                setIsMerging(false);

                console.log(`[HybridExport] 🟣 Constance merged: ${(finalBlob.size / 1024 / 1024).toFixed(1)} MB`);

                // ━━━━━━━━━ MODE 3: TRUE HYBRID (≥ 5 min, has FX) ━━━━━━━━━
            } else {
                const { buildHybridChunks } = await import('../utils/timeline-utils');
                const hybridChunks = buildHybridChunks(allClips, tracks);

                if (hybridChunks.length === 0) throw new Error('Aucun chunk à exporter');

                // Initialize chunk state for UI
                const initialChunkState: (ChunkStatus & { mode: 'local' | 'server' })[] = hybridChunks.map(hc => ({
                    index: hc.index,
                    mode: hc.mode,
                    serverStatus: 'pending',
                    serverProgress: 0,
                    url: null,
                    localStatus: 'waiting',
                    localProgress: 0,
                    size: 0,
                }));
                setChunks(initialChunkState);
                setIsMerging(false);

                const localCount = hybridChunks.filter(c => c.mode === 'local').length;
                const serverCount = hybridChunks.filter(c => c.mode === 'server').length;
                // `/export` keeps the HTTP request open for the full server render.
                // We use a stagger delay in runWithConcurrency to avoid hitting Cloud Run rate limits,
                // so we can safely process multiple chunks in parallel.
                const maxServerParallel = serverCount > 0 ? Math.min(3, serverCount) : 0;
                updateJob({
                    currentStep: `🔶 ${localCount} ⚡Web + ${serverCount} ☁Cloud${maxServerParallel > 0 ? ` (max ${maxServerParallel} en parallèle)` : ''}...`
                });

                const updateChunk = updateChunkAtIndex;

                hybridChunkPropsRef.current = {};

                const runWithConcurrency = async <T,>(taskFactories: Array<() => Promise<T>>, concurrency: number): Promise<T[]> => {
                    if (taskFactories.length === 0) return [];

                    const results: T[] = new Array(taskFactories.length);
                    let nextTaskIndex = 0;
                    const workerCount = Math.min(concurrency, taskFactories.length);

                    const workers = Array.from({ length: workerCount }, async () => {
                        while (true) {
                            const currentTaskIndex = nextTaskIndex++;
                            if (currentTaskIndex >= taskFactories.length) break;
                            results[currentTaskIndex] = await taskFactories[currentTaskIndex]();
                        }
                    });

                    await Promise.all(workers);
                    return results;
                };

                const serverTaskFactories: Array<() => Promise<void>> = [];
                const localTaskFactories: Array<() => Promise<void>> = [];

                for (const chunk of hybridChunks) {
                    const sourceClips = chunk.mode === 'server'
                        ? allClips.filter(c => !c.clipRole)
                        : allClips;
                    const chunkClips = getClipsForChunk(sourceClips, chunk.start, chunk.end);

                    hybridChunkPropsRef.current[chunk.index] = {
                        mode: chunk.mode,
                        clips: chunkClips,
                        start: chunk.start,
                        end: chunk.end,
                        total: hybridChunks.length,
                        baseJobId: tempJobId
                    };

                    if (chunk.mode === 'server') {
                        serverTaskFactories.push(() => processServerChunk(
                            chunk.index,
                            client,
                            projectId,
                            chunkClips,
                            tracks,
                            activeSubtitleLanguage,
                            subtitleStyle,
                            exportAspectRatio,
                            updateChunk,
                            hybridChunks.length,
                            chunk.start,
                            chunk.end,
                            tempJobId,
                            user.uid
                        ));
                    } else {
                        localTaskFactories.push(() => processLocalChunk(
                            chunk.index,
                            chunkClips,
                            tracks,
                            activeSubtitleLanguage,
                            subtitleStyle,
                            exportAspectRatio,
                            updateChunk
                        ));
                    }
                }

                if (serverTaskFactories.length > 0) {
                    remoteWorkStarted = true;
                }

                await Promise.all([
                    runWithConcurrency(localTaskFactories, 1),
                    runWithConcurrency(serverTaskFactories, Math.max(1, maxServerParallel))
                ]);

                // Merge all chunks via Cloud Run
                updateJob({ currentStep: '☁️ Préparation de la fusion en ligne...', progress: 90 });
                setIsMerging(true);

                const chunkUrls: string[] = [];

                for (let i = 0; i < hybridChunks.length; i++) {
                    const chunk = chunksRef.current[i];
                    if (chunk.mode === 'server' && chunk.url) {
                        chunkUrls.push(chunk.url);
                    } else if (chunk.mode === 'local' && chunk.blob) {
                        updateJob({ currentStep: `☁️ Upload du segment web ${i + 1}/${hybridChunks.length}...`, progress: 90 });
                        const file = new File([chunk.blob], `local_chunk_${i}.mp4`, { type: 'video/mp4' });
                        const uploadedUrl = await uploadAssetToStorage(file, `chunk_${tempJobId}_${i}`, undefined, currentProjectIdRef.current);
                        if (!uploadedUrl) throw new Error(`Upload raté pour le chunk local ${i}`);
                        chunkUrls.push(uploadedUrl);
                    } else {
                        throw new Error(`Chunk ${i} is missing video data or URL`);
                    }
                }

                updateJob({ currentStep: '🔗 Assemblage final sur le Cloud...', progress: 95 });
                const finalRemoteUrl = await client.submitAsyncConcatJob(currentProjectIdRef.current, chunkUrls, `exports/${user.uid}/${tempJobId}_hybrid.mp4`);

                updateJob({ currentStep: '📥 Téléchargement du résultat final...', progress: 98 });
                const finalRes = await fetch(finalRemoteUrl);
                if (!finalRes.ok) throw new Error('Echec du téléchargement du fichier final hybrid depuis R2');
                finalBlob = await finalRes.blob();

                setIsMerging(false);

                console.log(`[HybridExport] 🔶 True Hybrid merged via Cloud Run: ${(finalBlob.size / 1024 / 1024).toFixed(1)} MB`);
            }

            // ━━━━━━━━━ SAVE + UPLOAD (shared for all modes) ━━━━━━━━━
            await validateExportBlob(finalBlob);
            const urlMP4 = URL.createObjectURL(finalBlob);
            const thumbnailUrl = await generateThumbnailFromUrl(urlMP4);
            const now = new Date();
            const baseName = `${mode === 'full-web' ? 'Web' : mode === 'constance' ? 'Constance' : 'Hybrid'}_${now.toISOString().split('T')[0]}_${String(now.getHours()).padStart(2, '0')}h${String(now.getMinutes()).padStart(2, '0')}`;

            const videoId = Date.now().toString();
            setExportedVideos(prev => [{
                id: videoId,
                name: baseName,
                urlMP4,
                thumbnailUrl: savedProjects.find(p => p.id === currentProjectIdRef.current)?.thumbnailUrl || thumbnailUrl,
            }, ...prev]);

            // ✅ Trigger download immediately — don't wait for R2 upload
            updateJob({ status: 'complete', progress: 100, currentStep: `${modeLabels[mode]} — Terminé !`, downloadUrl: urlMP4 });
            const a = document.createElement('a');
            a.href = urlMP4;
            a.download = `${baseName}.mp4`;
            a.click();
            console.log(`[HybridExport] ${modeLabels[mode]} download triggered: ${(finalBlob.size / 1024 / 1024).toFixed(1)} MB`);

            // Upload to R2 in background (fire-and-forget — don't block the user)
            (async () => {
                try {
                    const file = new File([finalBlob], `${baseName}.mp4`, { type: 'video/mp4' });
                    const r2Url = await uploadAssetToStorage(file);
                    if (r2Url && user) {
                        setExportedVideos(prev => prev.map(v => v.id === videoId ? { ...v, urlMP4: r2Url } : v));
                        const { doc, updateDoc, arrayUnion, getFirestore } = await import('firebase/firestore');
                        const db = getFirestore();
                        await updateDoc(doc(db, 'users', user.uid, 'projects', projectId), {
                            exports: arrayUnion({ id: videoId, name: baseName, urlMP4: r2Url })
                        });
                    }
                } catch (e) { console.warn('[HybridExport] R2 upload failed (background):', e); }
            })();

        } catch (error: any) {
            console.error('[HybridExport] Failed:', error);
            if (remoteWorkStarted) {
                updateJob({
                    status: 'failed',
                    currentStep: 'Le job cloud existe deja. Utiliser Retry Stuck / reprise sur ce job, pas une nouvelle relance.'
                });
            } else {
                // Remove the failed hybrid job from UI since we are falling back to the standard server export
                setActiveServerExports(prev => prev.filter(j => j.jobId !== tempJobId));
                // Reliability fallback: route to cloud server export only if no remote job was started yet.
                await handleStartServerExport({ mode: 'full', burnSubtitles: true, quality: 'standard' });
            }
        } finally {
            setHybridMode(false);
            setIsExporting(false);
        }
    }, [
        allClips,
        tracks,
        activeSubtitleLanguage,
        subtitleStyle,
        user,
        exportAspectRatio,
        scaleMode,
        savedProjects,
        handleStartServerExport,
        updateChunkAtIndex,
        upsertChunkAtIndex,
        prepareTimelineSourceForBurnParallel
    ]);

    // Computed: detect if timeline has overlays for hybrid export eligibility
    const hasTimelineOverlays = useMemo(() => {
        return detectOverlayClips(allClips, tracks).hasOverlays;
    }, [allClips, tracks]);

    // Computed: total timeline duration in seconds
    const totalTimelineDuration = useMemo(() => {
        if (allClips.length === 0) return 0;
        const validDurations = allClips.map(c => {
            const start = Number(c.start) || 0;
            const duration = Number(c.duration) || 0;
            return start + duration;
        }).filter(d => !isNaN(d));
        return validDurations.length > 0 ? Math.max(...validDurations, 0) : 0;
    }, [allClips]);

    return {
        // State
        isExporting, setIsExporting,
        exportStatus, setExportStatus,
        exportProgress, setExportProgress,
        exportDetails, setExportDetails,
        exportedVideos, setExportedVideos,
        exportAspectRatio, setExportAspectRatio,
        scaleMode, setScaleMode,
        vfxSegmentPreviews, setVfxSegmentPreviews,
        activeServerExports, // New Array State
        serverExportStatus: activeServerExports.length > 0 ? activeServerExports[0] : { isActive: false }, // Backwards compatibility if needed, though we should update UI
        exportStatusByProject, // Per-project status for sidebar badges

        // Client-side chunk manager
        chunks,
        isMerging,
        retryChunkDownload,

        // Hybrid export
        hasTimelineOverlays,
        handleStartHybridExport,
        totalTimelineDuration,

        // Refs
        exportManagerRef,
        serverExportUnsubscribesRef,

        // Computed
        isTimelineEligibleForFastExport,

        // Handlers
        handleStartExport,
        handleCancelExport,
        handleStartServerExport,
        handleCancelServerExport,
        handleDeleteExport,
        checkActiveExportJobs,
        resetExportState,
        uploadAssetToStorage,
        isBusy: isExporting || activeServerExports.some(j => j.isActive),
    };
};
